/**
 * Sectors (/industries) — commercial presentation layer (FR/EN).
 * Visual composition follows Owner HD mockups (PRESERVED).
 * Product truth = portfolio-policy + portfolio-intelligence (INTERNAL).
 * Public copy = commercial translation of that truth — not an audit report.
 *
 * Route canonical: /industries (nav label « Secteurs » / « Industries »).
 * No /sectors alias in this WP — document-only decision.
 *
 * INTERNAL_STATUS → PUBLIC_PRESENTATION:
 * - principal → no public badge (portfolio importance only)
 * - limited → optional "Disponibilité limitée" / "Limited availability"
 * - evolving / project / hidden → never sold as available products
 */
import type { PepLocale } from "../i18n/types";

/** Publishable product slugs only — never HIDDEN (comex, crm, insurance, legal…). */
export const SECTORS_PUBLISHABLE_PRODUCT_SLUGS = new Set([
  "edu",
  "rh",
  "mineportal",
  "inventory",
  "erp",
  "billing",
  "hospitol",
  "security",
]);

/** Catalog industry slugs that must NOT be promoted as standalone primary cards. */
export const SECTORS_HIDDEN_FROM_PUBLIC_OFFER = new Set([
  "comex",
  "crm",
  "insurance",
  "legal",
  "finance",
  "notifications",
  "autoid",
  "subportal",
  "biometric-gateway",
  "product",
]);

/** Internal portfolio maturity — not automatically a public badge. */
export type SectorMaturity = "principal" | "limited" | "evolving";

export type SectorProductRef = {
  slug: string;
  labelFr: string;
  labelEn: string;
  /** Internal product maturity; public UI shows limitedAvailabilityLabel when limited. */
  badge?: "limited" | "principal";
  href: string;
};

export type SectorAxis = {
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
};

export type PrimarySectorId =
  | "education"
  | "mining"
  | "health"
  | "commerce"
  | "security";

export type TransversalSectorId =
  | "hr"
  | "public"
  | "ngo"
  | "professional";

export interface SectorHeroStrip {
  id: PrimarySectorId;
  imageSrc: string;
  objectPositionDesktop: string;
  objectPositionTablet: string;
  objectPositionMobile: string;
  labelFr: string;
  labelEn: string;
  captionFr: string;
  captionEn: string;
  altFr: string;
  altEn: string;
  subject: string;
  focalPoint: string;
}

export interface PrimarySectorEditorial {
  id: PrimarySectorId;
  industrySlug: string;
  order: number;
  /** Internal only — do not auto-render PRINCIPAL publicly. */
  maturity: SectorMaturity;
  /** When true, show public limited-availability badge on the sector card. */
  showPublicLimitedBadge: boolean;
  tone: PrimarySectorId;
  imageSrc: string;
  objectPositionDesktop: string;
  objectPositionTablet: string;
  objectPositionMobile: string;
  titleFr: string;
  titleEn: string;
  summaryFr: string;
  summaryEn: string;
  /** Max 3 scannable business axes — proven capabilities only. */
  axes: readonly SectorAxis[];
  solutions: readonly SectorProductRef[];
  offerBadges?: readonly SectorProductRef[];
  imageAltFr: string;
  imageAltEn: string;
  subject: string;
  focalPoint: string;
  seoTermsFr: readonly string[];
  seoTermsEn: readonly string[];
}

export interface TransversalSectorEditorial {
  id: TransversalSectorId;
  industrySlug: string | null;
  maturity: SectorMaturity;
  showPublicLimitedBadge: boolean;
  titleFr: string;
  titleEn: string;
  summaryFr: string;
  summaryEn: string;
  solutions: readonly SectorProductRef[];
  /** Owner note: hr is a business function more than a market sector. */
  taxonomyNote?: string;
}

export const SECTORS_HERO_STRIPS: readonly SectorHeroStrip[] = [
  {
    id: "education",
    imageSrc: "/assets/home/home-solution-edu.webp",
    objectPositionDesktop: "42% 35%",
    objectPositionTablet: "40% 32%",
    objectPositionMobile: "38% 28%",
    labelFr: "Éducation",
    labelEn: "Education",
    captionFr: "Élever la qualité et la performance",
    captionEn: "Raise quality and performance",
    altFr: "Classe scolaire — élèves et enseignant en activité.",
    altEn: "Classroom — students and teacher at work.",
    subject: "classroom learning",
    focalPoint: "students + teacher mid-frame",
  },
  {
    id: "mining",
    imageSrc: "/assets/home/home-solution-mineportal.webp",
    objectPositionDesktop: "78% 45%",
    objectPositionTablet: "75% 42%",
    objectPositionMobile: "70% 40%",
    labelFr: "Mines & métaux",
    labelEn: "Mining & metals",
    captionFr: "Opérer en sécurité, avec efficacité",
    captionEn: "Operate safely and efficiently",
    altFr: "Engin minier sur site d'exploitation.",
    altEn: "Haul truck on a mining site.",
    subject: "mining haul truck",
    focalPoint: "yellow truck right-center",
  },
  {
    id: "health",
    imageSrc: "/assets/home/home-solution-hospitol.webp",
    objectPositionDesktop: "48% 30%",
    objectPositionTablet: "45% 28%",
    objectPositionMobile: "42% 25%",
    labelFr: "Santé",
    labelEn: "Healthcare",
    captionFr: "Soigner, gérer et protéger",
    captionEn: "Care, manage, and protect",
    altFr: "Professionnel de santé inspectant un médicament / stock pharmaceutique.",
    altEn: "Healthcare professional inspecting medicine / pharmacy stock.",
    subject: "pharmacy / clinical care",
    focalPoint: "hands + medication package",
  },
  {
    id: "commerce",
    imageSrc: "/assets/home/home-solution-inventory.webp",
    objectPositionDesktop: "40% 40%",
    objectPositionTablet: "38% 38%",
    objectPositionMobile: "35% 36%",
    labelFr: "Commerce & distribution",
    labelEn: "Trade & distribution",
    captionFr: "Stocker, approvisionner, distribuer",
    captionEn: "Store, replenish, distribute",
    altFr: "Allée d'entrepôt / dépôt avec rayonnages.",
    altEn: "Warehouse aisle with shelving.",
    subject: "warehouse aisle",
    focalPoint: "central aisle depth",
  },
  {
    id: "security",
    imageSrc: "/assets/home/home-solution-security.webp",
    objectPositionDesktop: "55% 35%",
    objectPositionTablet: "52% 32%",
    objectPositionMobile: "50% 30%",
    labelFr: "Sécurité privée",
    labelEn: "Private security",
    captionFr: "Surveiller, réagir, protéger",
    captionEn: "Monitor, respond, protect",
    altFr: "Agent de sécurité devant un mur d'écrans de vidéosurveillance.",
    altEn: "Security officer at a monitoring station.",
    subject: "security monitoring room",
    focalPoint: "officer + monitor wall",
  },
];

const ref = (
  slug: string,
  fr: string,
  en: string,
  badge?: "limited" | "principal",
  href?: string,
): SectorProductRef => ({
  slug,
  labelFr: fr,
  labelEn: en,
  badge,
  href: href ?? `/products/${slug}`,
});

export const SECTORS_PRIMARY: readonly PrimarySectorEditorial[] = [
  {
    id: "education",
    industrySlug: "education",
    order: 1,
    maturity: "principal",
    showPublicLimitedBadge: false,
    tone: "education",
    imageSrc: "/assets/home/home-solution-edu.webp",
    objectPositionDesktop: "42% 38%",
    objectPositionTablet: "40% 34%",
    objectPositionMobile: "38% 30%",
    titleFr: "Éducation",
    titleEn: "Education",
    summaryFr:
      "Pilotez votre établissement, de l'inscription au suivi des familles — sans empiler des outils isolés.",
    summaryEn:
      "Run your school from enrolment to family follow-up — without stacking disconnected tools.",
    axes: [
      {
        titleFr: "Scolarité",
        titleEn: "School life",
        bodyFr: "Inscriptions, classes et parcours des élèves au quotidien.",
        bodyEn: "Enrolments, classes, and learner journeys day to day.",
      },
      {
        titleFr: "Pédagogie",
        titleEn: "Teaching",
        bodyFr: "Préparation des cours, journal et suivi des évaluations.",
        bodyEn: "Lesson prep, teaching journals, and assessment follow-up.",
      },
      {
        titleFr: "Administration",
        titleEn: "Administration",
        bodyFr: "Frais de scolarité, personnel et communication avec les familles.",
        bodyEn: "School fees, staff, and communication with families.",
      },
    ],
    solutions: [
      ref("edu", "Edu", "Edu"),
      ref("rh", "RH", "HR"),
      ref("billing", "Invoice", "Invoice"),
    ],
    imageAltFr: "Établissement scolaire — activité de classe.",
    imageAltEn: "School setting — classroom activity.",
    subject: "education operations",
    focalPoint: "learners in classroom",
    seoTermsFr: [
      "logiciel gestion école RDC",
      "logiciel de gestion scolaire",
      "gestion d'établissement scolaire",
    ],
    seoTermsEn: ["school management software DRC", "school administration", "school fees software"],
  },
  {
    id: "mining",
    industrySlug: "rdc-mining",
    order: 2,
    maturity: "principal",
    showPublicLimitedBadge: false,
    tone: "mining",
    imageSrc: "/assets/home/home-solution-mineportal.webp",
    objectPositionDesktop: "78% 48%",
    objectPositionTablet: "74% 44%",
    objectPositionMobile: "70% 42%",
    titleFr: "Mines & métaux",
    titleEn: "Mining & metals",
    summaryFr:
      "Pilotez sites, magasins et équipes sur le terrain — en composant les produits ITaaS adaptés à vos opérations.",
    summaryEn:
      "Run sites, stores, and field teams — by composing the ITaaS products that fit your operations.",
    axes: [
      {
        titleFr: "Sites & opérations",
        titleEn: "Sites & operations",
        bodyFr: "Centralisez la vue des sites et des activités opérationnelles.",
        bodyEn: "Centralize site views and day-to-day operational activity.",
      },
      {
        titleFr: "Magasins & stock",
        titleEn: "Stores & stock",
        bodyFr: "Suivez le stock dépôt / magasin et les mouvements entre sites.",
        bodyEn: "Track depot and store stock, and movements between sites.",
      },
      {
        titleFr: "Équipes & facturation",
        titleEn: "Teams & invoicing",
        bodyFr: "Gérez effectifs et pointages, puis facturez vos prestations.",
        bodyEn: "Manage headcount and attendance, then invoice your services.",
      },
    ],
    solutions: [
      ref("mineportal", "MinePortal", "MinePortal"),
      ref("inventory", "Inventory", "Inventory"),
      ref("rh", "RH", "HR"),
      ref("billing", "Invoice", "Invoice"),
    ],
    imageAltFr: "Site minier — engin et opérations terrain.",
    imageAltEn: "Mining site — equipment and field operations.",
    subject: "mining operations",
    focalPoint: "haul truck",
    seoTermsFr: ["logiciel pour entreprise minière", "gestion des opérations minières", "magasin minier"],
    seoTermsEn: ["mining company software", "mining operations management", "mining store inventory"],
  },
  {
    id: "health",
    industrySlug: "healthcare",
    order: 3,
    maturity: "principal",
    showPublicLimitedBadge: false,
    tone: "health",
    imageSrc: "/assets/home/home-solution-hospitol.webp",
    objectPositionDesktop: "48% 32%",
    objectPositionTablet: "45% 28%",
    objectPositionMobile: "42% 26%",
    titleFr: "Santé",
    titleEn: "Healthcare",
    summaryFr:
      "Démarrez par la pharmacie et le stock médical. Certaines capacités hospitalières avancées sont proposées dans un périmètre contrôlé.",
    summaryEn:
      "Start with pharmacy and medical stock. Selected advanced hospital capabilities are offered within a controlled scope.",
    axes: [
      {
        titleFr: "Pharmacie",
        titleEn: "Pharmacy",
        bodyFr: "Ventes au comptoir, files d'ordonnances et impression d'étiquettes.",
        bodyEn: "Counter sales, prescription queues, and label printing.",
      },
      {
        titleFr: "Stock médical",
        titleEn: "Medical stock",
        bodyFr: "Réceptions, sorties, alertes et suivi des niveaux de stock.",
        bodyEn: "Receipts, issues, alerts, and stock-level follow-up.",
      },
      {
        titleFr: "Organisation",
        titleEn: "Organization",
        bodyFr: "Composez RH soignants et facturation selon votre besoin.",
        bodyEn: "Compose clinical HR and invoicing to your needs.",
      },
    ],
    solutions: [
      ref("hospitol", "Hospitol", "Hospitol", "limited"),
      ref("inventory", "Inventory", "Inventory"),
      ref("rh", "RH", "HR"),
      ref("billing", "Invoice", "Invoice"),
    ],
    offerBadges: [
      {
        slug: "pharmacy-offer",
        labelFr: "ITaaS Pharmacy",
        labelEn: "ITaaS Pharmacy",
        badge: "limited",
        href: "/industries/healthcare",
      },
      {
        slug: "hospitol",
        labelFr: "ITaaS Hospitol",
        labelEn: "ITaaS Hospitol",
        badge: "limited",
        href: "/products/hospitol",
      },
    ],
    imageAltFr: "Contexte pharmacie / soin — inspection de médicaments.",
    imageAltEn: "Pharmacy / care context — inspecting medicines.",
    subject: "pharmacy operations",
    focalPoint: "medication package in hands",
    seoTermsFr: ["logiciel pharmacie", "gestion de stock pharmacie", "pharmacie hospitalière"],
    seoTermsEn: ["pharmacy software", "pharmacy inventory management", "hospital pharmacy"],
  },
  {
    id: "commerce",
    industrySlug: "retail-distribution",
    order: 4,
    maturity: "principal",
    showPublicLimitedBadge: false,
    tone: "commerce",
    imageSrc: "/assets/home/home-solution-inventory.webp",
    objectPositionDesktop: "40% 42%",
    objectPositionTablet: "38% 40%",
    objectPositionMobile: "35% 38%",
    titleFr: "Commerce & distribution",
    titleEn: "Trade & distribution",
    summaryFr:
      "Suivez les stocks dans vos dépôts et magasins, déplacez les articles entre sites, et couplez facturation et organisation.",
    summaryEn:
      "Track stock in depots and stores, move goods between sites, and pair invoicing with organization.",
    axes: [
      {
        titleFr: "Dépôts & magasins",
        titleEn: "Depots & stores",
        bodyFr: "Pilotez entrepôts, dépôts et magasins avec zones et emplacements.",
        bodyEn: "Run warehouses, depots, and stores with zones and locations.",
      },
      {
        titleFr: "Mouvements",
        titleEn: "Stock movements",
        bodyFr: "Enregistrez réceptions, sorties, transferts, retours et ajustements.",
        bodyEn: "Record receipts, issues, transfers, returns, and adjustments.",
      },
      {
        titleFr: "Pilotage",
        titleEn: "Control",
        bodyFr: "Alertes de stock bas, réconciliation et facturation clients.",
        bodyEn: "Low-stock alerts, reconciliation, and customer invoicing.",
      },
    ],
    solutions: [
      ref("inventory", "Inventory", "Inventory"),
      ref("billing", "Invoice", "Invoice"),
      ref("erp", "ERP", "ERP"),
    ],
    imageAltFr: "Entrepôt / dépôt — stockage et préparation.",
    imageAltEn: "Warehouse / depot — storage and picking.",
    subject: "warehouse stock operations",
    focalPoint: "aisle perspective",
    seoTermsFr: ["gestion stock magasin", "gestion d'entrepôt", "logiciel de stock"],
    seoTermsEn: ["store inventory management", "warehouse management", "inventory software"],
  },
  {
    id: "security",
    industrySlug: "security-services",
    order: 5,
    maturity: "limited",
    showPublicLimitedBadge: true,
    tone: "security",
    imageSrc: "/assets/home/home-solution-security.webp",
    objectPositionDesktop: "55% 38%",
    objectPositionTablet: "52% 34%",
    objectPositionMobile: "50% 32%",
    titleFr: "Sécurité privée",
    titleEn: "Private security",
    summaryFr:
      "Structurez clients, sites et postes pour démarrer votre exploitation. Certaines fonctions avancées restent proposées dans un périmètre limité.",
    summaryEn:
      "Structure clients, sites, and posts to start your operations. Selected advanced functions remain available within a limited scope.",
    axes: [
      {
        titleFr: "Clients & sites",
        titleEn: "Clients & sites",
        bodyFr: "Référencez vos clients et vos sites d'intervention.",
        bodyEn: "Register your clients and intervention sites.",
      },
      {
        titleFr: "Postes",
        titleEn: "Posts",
        bodyFr: "Organisez le déploiement opérationnel de base par poste.",
        bodyEn: "Organize baseline operational deployment by post.",
      },
      {
        titleFr: "Composition",
        titleEn: "Composition",
        bodyFr: "Ajoutez RH et stock d'équipements selon votre besoin.",
        bodyEn: "Add HR and equipment stock when your operation needs them.",
      },
    ],
    solutions: [ref("security", "Security", "Security", "limited")],
    imageAltFr: "Poste de surveillance — écrans et agent.",
    imageAltEn: "Monitoring post — screens and officer.",
    subject: "private security ops",
    focalPoint: "officer facing monitors",
    seoTermsFr: ["logiciel sécurité privée", "gestion gardiennage", "sites et postes de sécurité"],
    seoTermsEn: ["private security software", "guarding management", "security sites and posts"],
  },
];

export const SECTORS_TRANSVERSAL: readonly TransversalSectorEditorial[] = [
  {
    id: "hr",
    industrySlug: null,
    maturity: "limited",
    showPublicLimitedBadge: false,
    titleFr: "Ressources humaines",
    titleEn: "Human resources",
    summaryFr: "Effectifs, pointage et recrutement pour vos équipes — selon vos métiers et processus.",
    summaryEn: "Workforce, attendance, and recruitment for your teams — fitted to your roles and processes.",
    solutions: [
      ref("rh", "RH · Workforce · Attendance · Recruitment", "HR · Workforce · Attendance · Recruitment"),
    ],
    taxonomyNote:
      "RH is closer to a business function than a market sector; kept as transversal context pending Owner taxonomy decision.",
  },
  {
    id: "public",
    industrySlug: "ngo-public",
    maturity: "limited",
    showPublicLimitedBadge: false,
    titleFr: "Secteur public",
    titleEn: "Public sector",
    summaryFr: "Organisation, facturation et RH pour accompagner vos structures publiques.",
    summaryEn: "Organization, invoicing, and HR to support your public bodies.",
    solutions: [
      ref("erp", "ERP", "ERP"),
      ref("billing", "Invoice", "Invoice"),
      ref("rh", "RH", "HR"),
    ],
  },
  {
    id: "ngo",
    industrySlug: "ngo-public",
    maturity: "limited",
    showPublicLimitedBadge: false,
    titleFr: "ONG & organisations",
    titleEn: "NGOs & organizations",
    summaryFr: "Pilotez stock, équipes et facturation de projets avec les produits ITaaS adaptés.",
    summaryEn: "Run project stock, teams, and invoicing with the ITaaS products that fit.",
    solutions: [
      ref("erp", "ERP", "ERP"),
      ref("inventory", "Inventory", "Inventory"),
      ref("rh", "RH", "HR"),
      ref("billing", "Invoice", "Invoice"),
    ],
  },
  {
    id: "professional",
    industrySlug: "professional-services",
    maturity: "limited",
    showPublicLimitedBadge: false,
    titleFr: "Services professionnels",
    titleEn: "Professional services",
    summaryFr: "Organisation, RH et facturation pour cabinets et prestataires.",
    summaryEn: "Organization, HR, and invoicing for firms and service providers.",
    solutions: [
      ref("erp", "ERP", "ERP"),
      ref("rh", "RH", "HR"),
      ref("billing", "Invoice", "Invoice"),
    ],
  },
];

export const SECTORS_APPROACH_STEPS = [
  {
    id: "challenges",
    titleFr: "Vos enjeux métier",
    titleEn: "Your business needs",
    bodyFr: "Nous partons de votre réalité : école, mine, pharmacie, dépôt ou site de sécurité.",
    bodyEn: "We start from your reality: school, mine, pharmacy, depot, or security site.",
  },
  {
    id: "capabilities",
    titleFr: "Les capacités adaptées",
    titleEn: "The right capabilities",
    bodyFr: "Nous identifions ce qui répond vraiment à vos opérations.",
    bodyEn: "We identify what truly matches your operations.",
  },
  {
    id: "products",
    titleFr: "Une composition de produits",
    titleEn: "A product composition",
    bodyFr: "Nous assemblons les logiciels ITaaS utiles à votre périmètre.",
    bodyEn: "We assemble the ITaaS software useful for your scope.",
  },
  {
    id: "results",
    titleFr: "Déploiement & accompagnement",
    titleEn: "Deployment & support",
    bodyFr: "Nous déployons, accompagnons vos équipes et faisons évoluer la solution avec vous.",
    bodyEn: "We deploy, support your teams, and grow the solution with you.",
  },
] as const;

/** Public label for LIMITED internal status — never expose raw PRINCIPAL/GA/PROJECT. */
export function publicLimitedLabel(locale: PepLocale): string {
  return locale === "en" ? "Limited availability" : "Disponibilité limitée";
}

export function sectorsCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en
      ? "Industries we support — education, mining, healthcare, trade & security"
      : "Les secteurs que nous accompagnons — éducation, mines, santé, commerce et sécurité",
    seoDescription: en
      ? "See how ITaaS Suite helps organizations in education, mining, healthcare, trade, and private security — with school management, pharmacy stock, warehouse inventory, and more."
      : "Découvrez comment ITaaS Suite accompagne les organisations en éducation, mines, santé, commerce et sécurité privée — gestion scolaire, stock pharmacie, stock entrepôt et plus.",
    seoKeywords: en
      ? [
          "school management software DRC",
          "mining company software",
          "pharmacy software",
          "warehouse management",
          "store inventory management",
          "HR software",
          "invoicing software",
          "private security software",
        ]
      : [
          "logiciel gestion école RDC",
          "logiciel pour entreprise minière",
          "logiciel pharmacie",
          "gestion d'entrepôt",
          "gestion stock magasin",
          "logiciel ressources humaines",
          "logiciel facturation",
          "logiciel sécurité privée",
        ],
    heroTitleBefore: en ? "Solutions tailored to your " : "Des solutions adaptées à votre ",
    heroTitleEmphasis: en ? "sector" : "secteur",
    heroTitleAfter: en ? ", and your challenges." : ", à vos enjeux.",
    heroLead: en
      ? "ITaaS Suite adapts its products and capabilities to the realities of different sectors — for public and private organizations."
      : "ITaaS Suite adapte ses produits et capacités aux réalités de différents secteurs — pour les organisations publiques et privées.",
    pillars: [
      {
        id: "adapted",
        title: en ? "Adapted" : "Adaptées",
        body: en ? "To your profession and processes" : "À votre métier et vos processus",
      },
      {
        id: "secure",
        title: en ? "Controlled" : "Sécurisées",
        body: en ? "Data and operations under control" : "Données et opérations sous contrôle",
      },
      {
        id: "scalable",
        title: en ? "Evolving" : "Évolutives",
        body: en ? "From essential to complete, at your pace" : "De l'essentiel au complet, à votre rythme",
      },
    ],
    primaryEyebrow: en ? "Industries we support" : "Les secteurs que nous accompagnons",
    primaryTitleBefore: en ? "Industries we " : "Les secteurs que nous ",
    primaryTitleEmphasis: en ? "support" : "accompagnons",
    primaryTitleAfter: "",
    primaryLead: en
      ? "Identify your sector, see what you can run with ITaaS, and discover the products that contribute."
      : "Identifiez votre secteur, voyez ce que vous pouvez piloter avec ITaaS, et découvrez les produits qui y contribuent.",
    transversalEyebrow: en ? "Also fitted to your organization" : "Aussi adaptées à votre organisation",
    transversalTitle: en
      ? "Solutions that also fit your organization"
      : "Des solutions qui s'adaptent aussi à votre organisation",
    transversalLead: en
      ? "Combine ITaaS capabilities according to your roles, teams, and processes."
      : "Combinez les capacités ITaaS selon vos métiers, vos équipes et vos processus.",
    seeAll: en ? "See all" : "Voir tout",
    keySolutions: en ? "ITaaS solutions" : "Solutions ITaaS",
    axesLabel: en ? "What you can run" : "Ce que vous pouvez piloter",
    approachTitle: en ? "How we work with your sector" : "Comment nous intervenons",
    approachLead: en
      ? "From your business need to a product configuration you can operate."
      : "De votre besoin métier à une configuration de produits exploitable.",
    approachBenefits: en
      ? [
          "Clarify your operational priorities",
          "Select the products that fit",
          "Configure and deploy with support",
          "Grow the scope as your organization evolves",
        ]
      : [
          "Clarifier vos priorités opérationnelles",
          "Sélectionner les produits adaptés",
          "Configurer et déployer avec accompagnement",
          "Élargir le périmètre à mesure que vous évoluez",
        ],
    conversionTitle: en ? "Is your sector missing?" : "Votre secteur ne figure pas ici ?",
    conversionLead: en
      ? "Talk to an expert to identify the ITaaS products and capabilities that fit your needs."
      : "Parlez à un expert pour identifier les produits et capacités ITaaS adaptés à vos besoins.",
    ctaExpert: en ? "Talk to an expert" : "Parler à un expert",
    ctaSolutions: en ? "See our solutions" : "Voir nos solutions",
    explore: en ? "Explore the sector" : "Explorer le secteur",
    limitedAvailability: publicLimitedLabel(locale),
    trust: [
      {
        id: "support",
        label: en ? "End-to-end support" : "Accompagnement de bout en bout",
      },
      {
        id: "secure",
        label: en ? "Secure deployments" : "Déploiements sécurisés",
      },
      {
        id: "results",
        label: en ? "Measurable results" : "Résultats mesurables",
      },
    ],
    contactHref: "/contact",
    solutionsHref: "/solutions",
    loading: en ? "Loading…" : "Chargement…",
    notFound: en ? "Sector not found." : "Secteur introuvable.",
    backToSectors: en ? "Back to sectors" : "Retour aux secteurs",
    challengesTitle: en ? "What you can run" : "Ce que vous pouvez piloter",
    recommendedTitle: en ? "Relevant products" : "Produits pertinents",
    breadcrumb: en ? "Sectors" : "Secteurs",
  };
}

/** Photo crop matrix for Owner review / QA. */
export const SECTORS_ASSET_MATRIX = SECTORS_PRIMARY.map((s) => ({
  asset: s.imageSrc,
  sector: s.id,
  subject: s.subject,
  focalPoint: s.focalPoint,
  desktopCrop: s.objectPositionDesktop,
  tabletCrop: s.objectPositionTablet,
  mobileCrop: s.objectPositionMobile,
  status: "VERIFIED_POSITIONING" as const,
}));
